import { FC } from 'react'
import { useIsFetching, useIsMutating } from '@tanstack/react-query'
import { useDelayedLoading } from '@hooks/useDelayedLoading'

const DELAY_MS = 2000

/**
 * Non-blocking indicator shown only for initial loads (queries without data yet)
 * or explicit mutations in flight for longer than {@link DELAY_MS}.
 * Background refetches are ignored; pointer-events-none keeps the UI usable.
 */
export const DelayedQueryLoadingOverlay: FC = () => {
  const fetching = useIsFetching({ predicate: (query) => query.state.status === 'pending' })
  const mutating = useIsMutating()
  const isBusy = fetching > 0 || mutating > 0
  const show = useDelayedLoading(isBusy, DELAY_MS)

  if (!show) return null

  return (
    <div
      className="pointer-events-none fixed bottom-4 right-4 z-[45] animate-in fade-in slide-in-from-bottom-2 duration-500"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <div className="animate-long-wait-breathe flex items-center gap-3 rounded-full border border-cofarte-purple/10 bg-white/90 px-4 py-2.5 shadow-cofarte-lg">
        <div className="relative h-5 w-5 shrink-0" aria-hidden>
          <div className="absolute inset-0 rounded-full border-2 border-cofarte-purple/12" />
          <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-cofarte-purple/45 animate-long-wait-spin" />
        </div>
        <p className="text-xs leading-relaxed text-muted-foreground">
          Estamos procesando su petición
        </p>
      </div>
    </div>
  )
}
